import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getRemotePcAgentListApi } from '~/features/remote-pc-agent/api/get-remote-pc-agent-list.api';
import { getRemotePcAgentUsageStatisticsApi } from '~/features/remote-pc-agent/api/get-remote-pc-agent-usage-statistics.api';
import { getRemotePcAgentApi } from '~/features/remote-pc-agent/api/get-remote-pc-agent.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type {
  RemotePcAgent,
  RemotePcAgentListParams,
  RemotePcAgentUsageStatistics,
  UsageStatisticsParams,
} from '~/features/remote-pc-agent/types/remote-pc-agent.type';

export interface RemotePcAgentState {
  isLoading: boolean;
  list: RemotePcAgent[];
  meta: PaginationMeta | null;
  params: RemotePcAgentListParams | null;
  selectedRemotePcAgent: RemotePcAgent | null;
  usageStatistics: RemotePcAgentUsageStatistics | null;
}

const initialState: RemotePcAgentState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedRemotePcAgent: null,
  usageStatistics: null,
};

/**
 * Store IP 목록 조회
 */
export const fetchRemotePcAgentList = createAsyncThunk<
  PaginatedApiResponse<RemotePcAgent>,
  RemotePcAgentListParams
>('remotePcAgent/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getRemotePcAgentListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * Store IP 상세 조회
 */
export const fetchRemotePcAgentDetail = createAsyncThunk<RemotePcAgent, number>(
  'remotePcAgent/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getRemotePcAgentApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Store IP 사용량 통계 조회
 */
export const fetchRemotePcAgentUsageStatistics = createAsyncThunk<
  RemotePcAgentUsageStatistics,
  UsageStatisticsParams | undefined
>('remotePcAgent/fetchUsageStatistics', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getRemotePcAgentUsageStatisticsApi(params);
    return data.data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

const RemotePcAgentSlice = createSlice({
  initialState,
  name: 'remotePcAgent',
  reducers: {
    setSelectedRemotePcAgent: (state, action) => {
      state.selectedRemotePcAgent = action.payload;
    },
    resetSelectedRemotePcAgent: state => {
      state.selectedRemotePcAgent = null;
    },
    resetRemotePcAgent: state => {
      state.selectedRemotePcAgent = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
      state.usageStatistics = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchRemotePcAgentList
      .addCase(fetchRemotePcAgentList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchRemotePcAgentList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchRemotePcAgentList.rejected, state => {
        state.isLoading = false;
      })
      // fetchRemotePcAgentDetail
      .addCase(fetchRemotePcAgentDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchRemotePcAgentDetail.fulfilled, (state, action) => {
        state.selectedRemotePcAgent = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchRemotePcAgentDetail.rejected, state => {
        state.isLoading = false;
      })
      // fetchRemotePcAgentUsageStatistics
      .addCase(fetchRemotePcAgentUsageStatistics.fulfilled, (state, action) => {
        state.usageStatistics = action.payload;
      });
  },
});

export const { setSelectedRemotePcAgent, resetSelectedRemotePcAgent, resetRemotePcAgent } =
  RemotePcAgentSlice.actions;
export default RemotePcAgentSlice;
